import React from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import NextAuthProvidersEnum from '@/const/auth';

import { FieldValues, useForm } from 'react-hook-form';

import { authService } from '@/api';
import { useNotification } from './useNotification';
import { UserPayload } from '@/interfaces';
import Logger from '@/utils/logger';
import {
	customEvent,
	formatErrorMessage,
	formatPhoneRemove,
	getDeviceInfo,
	getAttributionForApi,
	persistSignupAttributionCookie,
	setLeadQualificationOutcome,
} from '@/lib';
import {
	deleteLocalStorage,
	getLocalStorage,
	setLocalStorage,
} from '@/utils/localStorage';
import AppRoutes from '@/const/routes';

export const useAuth = () => {
	const router = useRouter();
	const logger = new Logger({ identifier: 'SignUp' });
	const { toastSuccess, toastError } = useNotification();

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [recaptchaValid, setRecaptchaValid] = React.useState<boolean | null>(
		null
	);

	const isStaging = process.env.NEXT_PUBLIC_SERVER === 'staging';
	const isDev = process.env.NEXT_PUBLIC_SERVER === 'develop';
	const caseValue = router.query['case_id'] || router.query['case'];
	const evaluated = router.query['evaluated'];
	// const {
	//   watch,
	//   register,
	//   setValue,
	//   handleSubmit,
	//   formState: { errors, isDirty, isValid },
	// } = useForm({ mode: 'onSubmit' });

	const form = useForm({ mode: 'onSubmit' });

	React.useEffect(() => {
		// Store the referrer (previous page URL) in sessionStorage for Google Analytics tracking.
		// This value can be sent later as a custom parameter (e.g., 'page_referrer') when logging events like 'create_account_click'.
		// We ensure the referrer exists and is different from the current page to avoid self-referrals.
		const ref = document.referrer;
		if (ref && ref !== window.location.href) {
			sessionStorage.setItem('page_referrer', ref);
		}
	}, []);

	React.useEffect(() => {
		if (isStaging || isDev) {
			setRecaptchaValid(true);
		}
	}, [isStaging, isDev]);

	React.useEffect(() => {
		if (evaluated === 'true' || evaluated === 'false') {
			const caseId = Array.isArray(caseValue) ? caseValue[0] : caseValue;
			if (typeof caseId === 'string') {
				setLeadQualificationOutcome(evaluated === 'true', caseId);
			}
		}
	}, [evaluated, caseValue]);

	const onChangeRecaptcha = () => {
		setRecaptchaValid(true);
	};

	// SUBMIT function
	const handleSubmitData = async (
		provider: NextAuthProvidersEnum,
		data?: FieldValues
	): Promise<void> => {
		setIsLoading(true);
		logger.info('Data: ', { data });

		try {
			const { deviceType, os } = getDeviceInfo();

			// Register with credentials
			if (provider === NextAuthProvidersEnum.CREDENTIALS && data) {
				const userIdPreApplication = sessionStorage.getItem(
					'user_id_application'
				);
				const transaction_id = getLocalStorage('transaction_id');

				// Build payload
				const attribution = getAttributionForApi();
				const userPayload: UserPayload = {
					email: data.email,
					phoneNumber: formatPhoneRemove(data.phoneNumber),
					password: data.password,
					confirmPassword: data.confirmPassword ?? data.password,
					ip_user: userIdPreApplication,
					everflow_transaction_id: transaction_id || undefined,
					...(attribution ? { attribution } : {}),
				};

				const signUpResponse = await authService.signUp(userPayload);

				// User created successfully
				if (signUpResponse.data.user.id) {
					deleteLocalStorage('transaction_id');
					setLocalStorage('user_id', signUpResponse.data.user.id); // save user id
					toastSuccess('You have successfully created your account.');

					// account_created is emitted by backend from /sign-up attribution
					// pushLeadJourneyEvent('account_created', {
					// 	dedupKey: `account_created:${signUpResponse.data.user.id}`,
					// 	extra: {
					// 		case_id: typeof caseId === 'string' ? caseId : undefined,
					// 		qualification_outcome:
					// 			evaluated === 'true'
					// 				? 'qualified'
					// 				: evaluated === 'false'
					// 				? 'not_qualified'
					// 				: undefined,
					// 	},
					// });

					let redirectRoute = `${AppRoutes.LANDING_CASES}`;

					const resSignIn = await signIn(provider, {
						...data,
						redirect: false,
					});

					if (resSignIn && resSignIn.status === 200) {
						redirectRoute =
							caseValue && evaluated
								? `${AppRoutes.CLIENTAPP_DASHBOARD}?evaluated=${evaluated}&case_id=${caseValue}`
								: AppRoutes.CLIENTAPP_DASHBOARD;
					}

					const pageReferrer =
						sessionStorage.getItem('page_referrer') || 'direct';
					sessionStorage.removeItem('user_id_application');

					// register the event in google analytics and redirect to
					customEvent({
						action: 'create_account_click',
						category: 'clientapp',
						label: 'Create account',
						value: 'Create account',
						pageReferrer: pageReferrer, //for Google Analytics tracking
						deviceType: deviceType,
						deviceOs: os,
						callback: () => router.push(redirectRoute),
					});
				} else {
					logger.error(signUpResponse);
				}
			} else {
				let redirectRoute: string = AppRoutes.CLIENTAPP_AUTH_SIGN_UP_SUCCESS;
				if (caseValue && evaluated) {
					redirectRoute = `${AppRoutes.CLIENTAPP_AUTH_SIGN_UP_SUCCESS}?evaluated=${evaluated}&case_id=${caseValue}`;
				}

				sessionStorage.setItem('social_signup', provider);
				persistSignupAttributionCookie();

				await signIn(provider, {
					redirect: true,
					callbackUrl: redirectRoute,
				});
			}
		} catch (error) {
			logger.error(error);
			toastError(formatErrorMessage((error as any)?.message || 'Error'));
		} finally {
			setIsLoading(false);
		}
	};

	return {
		form,
		isLoading,
		isStaging,
		isDev,
		recaptchaValid,
		onChangeRecaptcha,
		handleSubmitData,
	};
};
